import cn from 'classnames';
import React, { ElementType } from 'react';
import { createRoot } from 'react-dom/client';
import { invoke, merge, set } from 'lodash';
import ContentEditable from 'react-contenteditable';
import { connect, ConnectedProps } from 'react-redux';

import { location } from 'common/utils/url-helper';
import { analytics } from 'common/utils/analytics';
import { isLayerType } from 'common/utils/blocks/is-layer-type';
import { CardFacade, StoryFacade } from 'common/utils/facades';
import { pastePlainText, sleep, triggerFocus } from 'common/utils/helpers';
import { CARD_TYPE, COMPONENT_STATES, COMPONENT_TYPE, UNIFORM_PROPS } from 'common/constants';

import { StoryHistory } from 'client/utils';
import { ClientReducerState } from 'client/reducers';
import { getCardVotes, sendForm, sendAnswer } from 'client/actions/api';
import { selectCard } from 'client/reducers/card/selectors';
import { updateAnswers } from 'client/actions/update-answers';
import { setFormData, SetFormDataPayload } from 'client/actions/set-form-data';
import { transmitToAdminBbUpdate } from 'client/utils/transmit-to-admin-bb-update';
import { selectStory, selectStoryCardSettings } from 'client/reducers/story/selectors';
import { ScriptRenderer } from 'client/components/common/ScriptRenderer/ScriptRenderer';
import { selectTotalAnswersByCard, selectUserAnswersByCard } from 'client/reducers/user/selectors';
import { BBCommonProps, BBEditableModeProps, CardData, EditorMode, StoryModel, StorySettingsOfCard } from 'types/story';
import { CHILDREN_KEY, getElementIdByNodeId, getLinkProps } from 'client/components/common/BuildingBlocks/utils/common';
import { EventProviderContextT, withEventProvider } from 'client/components/pages/Story/EventProvider/Context';
import withCardTransitionContext from 'client/components/common/BuildingBlocks/BuildingBlockEnhancer';
import { withFormContext } from 'client/components/common/StoryCard/Form/withFormContext';
import { FIELD_DATA_ATTR } from 'client/components/common/BuildingBlocks/Fields/constants';
import { TimerEvEm } from 'client/components/common/BuildingBlocks/Timer/utils';
import { FormDataT } from 'client/components/common/StoryCard/Form/types';
import css from './Button.scss';

const { isPreview } = location.client;

const connector = connect(
	(state: ClientReducerState) => {
		const story = selectStory(state);
		const SF = new StoryFacade(story as StoryModel);
		const card = selectCard(state) as CardData;
		const CF = new CardFacade(card);
		const showResults = CF.getShowResults(SF.settings);
		const storyCardSettings = selectStoryCardSettings(state, CF.cardId);

		return {
			cardSettings: storyCardSettings,
			storyId: SF.storyId,
			cardType: CF.type,
			cardId: CF.cardId,
			userAnswers: selectUserAnswersByCard(state, CF.cardId),
			answersMin: CardFacade.getAnswersMin(CF.type, storyCardSettings),
			answersMax: CardFacade.getAnswersMax(CF.type, storyCardSettings),
			answersSelected: selectTotalAnswersByCard(state),
			showResults,
		};
	},
	{ sendForm, getCardVotes, updateAnswers, sendAnswer, setFormData }
);

type State = {
	html: string;
	htmlPrev: string;
	editing: boolean;
	submitted: boolean;
};

type Props = BBCommonProps & { className?: string } & ConnectedProps<typeof connector> & EventProviderContextT;

const toPlainText = (v: string) => v?.replace(/<[^>]*>?/gm, '');

class Button extends React.Component<Props, State> {
	editableRef = React.createRef<HTMLElement>();

	isClicked = false;

	static getDerivedStateFromProps({ children }, state) {
		const isUpdatedChildrenFromProp = children !== state.htmlPrev;
		if (isUpdatedChildrenFromProp) {
			return { html: children, htmlPrev: children };
		}
		return null;
	}

	state = {
		htmlPrev: this.props.children as string,
		html: this.props.children as string,
		editing: false,
		submitted: false,
	};

	componentDidUpdate = (prevProps: Props) => {
		const { cardId } = this.props;

		if (cardId !== prevProps.cardId && isLayerType(this.props).global) {
			this.isClicked = false;
		}
	};

	onEditableBlur = event => {
		const { path } = event.target.dataset;
		const { currentMediaQuery } = this.props;

		// NOTE: children is always writes to defaultState, but for certain media query
		transmitToAdminBbUpdate({
			id: Button.name,
			path: `elements.${path}.${CHILDREN_KEY}.${COMPONENT_STATES.DEFAULT}.${currentMediaQuery}`,
			value: this.state.html,
			isStory: isLayerType(this.props).global,
		});

		this.setState({ editing: false });
	};

	onEditableChange = event => {
		this.setState({ html: toPlainText(event.target.value) });
		// trigger SelectionHint update to set current editable node sizes
		triggerFocus(this.editableRef.current);
	};

	onEditableDoubleClick = () => {
		this.setState({ editing: true }, () => {
			// update SelectionHint and place cursor into contenteditable block
			triggerFocus(this.editableRef.current);
		});
	};

	get eventListeners() {
		if (this.props.isEditableMode) {
			return {};
		}

		return {
			onClick: async (event: React.MouseEvent<HTMLLinkElement | HTMLButtonElement>) => {
				if (this.isClicked) {
					return;
				}

				const { storyId, cardId } = this.props;

				analytics.onLinkClick({
					storyId,
					cardId,
					name: this.props.uiConfig.editorProps.name,
				});

				this.isClicked = true;

				invoke(this.props.eventListeners, 'onClick', event);

				const externalLink = this.props.uiConfig.componentProps[UNIFORM_PROPS.btnLink];
				const internalLink = externalLink ? '' : this.props.uiConfig.componentProps[UNIFORM_PROPS.btnNavLink];

				if (event.currentTarget.tagName === 'A' && !internalLink) {
					// it is external link, any event shouldn't be called
					return;
				}

				if (isFormSubmit(this.props)) {
					const fields = [
						...document.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>(`[${FIELD_DATA_ATTR}]`),
					].filter(isVisible);
					const formData = getFormData(fields, this.props.cardSettings ?? {});

					const { isValid } = await this.props.formContext?.validate?.(formData)!;

					if (!isValid) {
						this.isClicked = false;
						return;
					}

					try {
						this.setState({ submitted: true });
						// store form data in DB
						this.props.sendForm({
							storyId,
							cardId,
							formData: Object.values(formData),
						});

						const data = Object.entries(formData).reduce((acc, [fieldId, value]) => {
							acc[fieldId] = value.data;
							return acc;
						}, {} as SetFormDataPayload['data']);

						// store form data in redux store
						this.props.setFormData({ cardId, data });

						// collect submitted form data in local storage
						const sHistory = new StoryHistory({ storyId });
						sHistory.addFormData({ cardId, data });
					} catch (e) {
						this.setState({ submitted: false });
					}
				}

				if (isMultipleAnswerSubmit(this.props)) {
					if (!this.props.userAnswers) {
						return;
					}

					const { selected: selectedAnswerIds, data: selectedAnswersData } = this.props.userAnswers;
					const selectedAnswers = selectedAnswerIds.reduce((acc, answerId) => {
						acc[answerId] = { ...selectedAnswersData?.[answerId] };
						return acc;
					}, {} as { [answerId: string]: { textContent?: string; embedCode?: string } });

					// Stop timer component
					TimerEvEm.emit('pause', cardId);

					// submit selected answers to backend and get user votes
					let votes: NonNullable<Props['userAnswers']>['votes'];
					try {
						if (!isPreview) {
							this.props.sendAnswer({
								storyId,
								cardId,
								answerIds: selectedAnswerIds,
								answerText: selectedAnswerIds.map(id => selectedAnswersData?.[id]?.textContent ?? ''),
							});

							if (this.props.showResults) {
								const cardVotes = await this.props.getCardVotes({ storyId, cardId });
								if (cardVotes) votes = cardVotes;
							}
						}
						this.setState({ submitted: true });
					} catch (e) {
						this.setState({ submitted: false });
					}

					// Store votes and submit in redux
					this.props.updateAnswers({ cardId, votes, submitted: true });

					const sHistory = new StoryHistory({ storyId });

					// collect submitted answer in extra storage
					sHistory.addAnswer({
						cardId,
						answerIds: Object.keys(selectedAnswers),
					});

					Object.entries(selectedAnswers).forEach(([answerId, { embedCode }]) => {
						// render pixels for each answer
						if (!embedCode) return;
						const container = document.createElement('div');
						container.id = `answer-script-${cardId}-${answerId}`;
						document.body.appendChild(container);
						const root = createRoot(container);
						root.render(<ScriptRenderer value={embedCode} scriptOnly />);
					});

					// await user votes in percentages preview
					await sleep(1000);
				}

				if (isSingleAnswerSubmit(this.props)) {
					return;
				}

				this.props.eventProvider.events.flow(internalLink || this.props._id, { isDirectId: !!internalLink });
			},
		};
	}

	get isDisabled() {
		if (isFormSubmit(this.props)) return this.state.submitted;
		if (isMultipleAnswerSubmit(this.props)) return this.props.userAnswers?.submitted;
		return isSingleAnswerSubmit(this.props);
	}

	get isInactive() {
		return (
			isSingleAnswerSubmit(this.props) ||
			(isMultipleAnswerSubmit(this.props) && this.props.answersSelected < this.props.answersMin)
		);
	}

	renderEditable() {
		const { editableModeProps, uiConfig } = this.props;
		const nodeProps = merge({}, uiConfig.nodeProps, editableModeProps?.nodeProps);
		const isContentMode = this.props.editorMode === EditorMode.CONTENT;

		/**
		 * Why do we assign "updater" to className?
		 * This is a temporary fix, to update <ContentEditable> in case of
		 * some state was provided via nodeProps. Otherwise <ContentEditable> won't be
		 * updated, because of his own shouldComponentUpdate logic, will return false
		 * if there were changed some data attributes only.
		 */
		let updater = '';
		const updaterObj = {
			...nodeProps,
			...this.props.stateAttrs,
			...(isContentMode ? { 'data-content-mode': true } : null),
		};
		Object.keys(updaterObj).forEach(k => {
			const res = k.match(/^(data-)/);
			if (res) updater += `${k}=${updaterObj[k]}`;
		});

		return (
			<ContentEditable
				{...nodeProps}
				{...this.props.stateAttrs}
				{...this.props.eventListeners}
				innerRef={this.editableRef}
				className={cn(css.btn, css.editable, uiConfig.nodeProps.className, updater)}
				tagName="div"
				html={this.state.html}
				disabled={!this.state.editing}
				onDoubleClick={this.onEditableDoubleClick}
				onChange={this.onEditableChange}
				onBlur={this.onEditableBlur}
				onPaste={pastePlainText}
				{...(isContentMode
					? {
							onClick: e => {
								this.props.eventListeners?.onClick?.(e);
								this.onEditableDoubleClick();
							},
					  }
					: null)}
			/>
		);
	}

	renderDefault(props: Partial<BBEditableModeProps['nodeProps']> = {}) {
		const { isInactive } = this;
		const { nodeProps } = this.props.uiConfig;
		const { Component, ...componentProps } = getComponent(this.props);

		return (
			<Component
				{...componentProps}
				{...nodeProps}
				{...this.props.stateAttrs}
				{...this.props.eventListeners}
				{...this.eventListeners}
				{...props}
				className={cn(css.btn, nodeProps.className, props.className, {
					[css.inactive]: isInactive,
				})}
				/* eslint-disable-next-line react/no-danger */
				dangerouslySetInnerHTML={{ __html: this.state.html }}
				ref={this.props.containerRef}
				disabled={this.isDisabled || isInactive}
			/>
		);
	}

	render() {
		return this.props.isEditableMode ? this.renderEditable() : this.renderDefault();
	}
}

export function isFormSubmit(props: Pick<Props, 'uiConfig' | 'cardType'>) {
	// check UNIFORM_PROPS.btnSubmit instead of component.type to support old stories
	return !!props.uiConfig.componentProps[UNIFORM_PROPS.btnSubmit] && props.cardType === CARD_TYPE.FORM;
}

export function isAnswerSubmit(props: Pick<Props, 'cardType' | 'type'>) {
	return props.type === COMPONENT_TYPE.BUTTON_SUBMIT && CardFacade.hasFeature(props.cardType).answer;
}

export function isMultipleAnswerSubmit(props: Pick<Props, 'cardType' | 'answersMax' | 'type'>) {
	return isAnswerSubmit(props) && props.answersMax > 1;
}

export function isSingleAnswerSubmit(props: Pick<Props, 'cardType' | 'answersMax' | 'type'>) {
	return isAnswerSubmit(props) && props.answersMax === 1;
}

function isVisible(e: HTMLElement) {
	return !!(e.offsetWidth || e.offsetHeight || e.getClientRects().length);
}

function getComponent(props: Props) {
	const externalLink = props.uiConfig.componentProps[UNIFORM_PROPS.btnLink];

	if (externalLink && !isFormSubmit(props)) {
		return getLinkProps(props, { link: externalLink });
	}

	return {
		Component: 'button' as ElementType,
		type: 'button',
	};
}

function getFormData(fields: (HTMLInputElement | HTMLTextAreaElement)[], cardSettings: StorySettingsOfCard) {
	return fields.reduce<FormDataT>((memo, field) => {
		const { value, type } = field;
		const checked = 'checked' in field ? field.checked : undefined;
		const nodeId = field.dataset.bb ? field.id : field.closest('[data-bb]')?.id;
		const id = getElementIdByNodeId(nodeId) ?? '';
		const name = cardSettings.input?.[id]?.name || field.name;

		set(memo, id, {
			name,
			type,
			data: value,
			attributes: {
				id,
				...(type === 'checkbox' || type === 'radio' ? { checked } : null),
			},
		});

		return memo;
	}, {});
}

const WithRedux = connector(Button);
const WithFormContext = withFormContext(WithRedux);
const WithCardTransitionContext = withCardTransitionContext(WithFormContext);

export default withEventProvider(WithCardTransitionContext);
